import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "slick-carousel";
import "../assets/css/pages/Home.css";
import "../assets/css/Responsive/Home_Responsive.css";
import { fetchPageByDynamicRouting } from "../api/pages";
import PageSections from "../sections/PageSections";
import Lenis from "@studio-freight/lenis";
import { useSeo } from "../contexts/SeoContext";

/**
 * Smooth scroll using Lenis
 * - Same pattern as About.jsx
 * - Runs once on mount
 */
const useLenis = () => {
    useEffect(() => {
        if (typeof window === "undefined") return;

        // Disable native smooth scroll to avoid conflicts with Lenis
        document.documentElement.style.scrollBehavior = "auto";

        const lenis = new Lenis({
            duration: 1.5, // Increased for smoother scroll
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Smooth ease-out
            smoothWheel: true,
            smoothTouch: true, // Enable smooth touch for mobile
            wheelMultiplier: 1, // Adjust scroll sensitivity
            touchMultiplier: 1.5, // Adjust touch scroll sensitivity
            infinite: false,
        });

        // Prevent Lenis from intercepting wheel events on nested scrollable containers
        const handleWheel = (e) => {
            // Check for .hc-alert .timing container (vertical scroll)
            const timingContainer = e.target.closest(".hc-alert .timing");
            if (timingContainer) {
                // Batch read: cache layout properties to avoid multiple forced reflows
                const scrollHeight = timingContainer.scrollHeight;
                const clientHeight = timingContainer.clientHeight;
                const scrollTop = timingContainer.scrollTop;
                
                // Check if the container is scrollable (has overflow)
                const isScrollable = scrollHeight > clientHeight;

                if (isScrollable) {
                    // Check if we can scroll in the direction of the wheel event
                    const canScrollUp = scrollTop > 0;
                    const canScrollDown = scrollTop < scrollHeight - clientHeight - 1;

                    // If we can scroll in this direction, prevent page scroll and Lenis
                    if (
                        (e.deltaY < 0 && canScrollUp) ||
                        (e.deltaY > 0 && canScrollDown)
                    ) {
                        e.preventDefault(); // Prevent page scroll
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        return;
                    }
                }
            }

            // Check for .hc-alert-body container (horizontal scroll)
            const alertBody = e.target.closest(".hc-alert-body");
            if (alertBody) {
                // Batch read: cache layout properties to avoid multiple forced reflows
                const scrollWidth = alertBody.scrollWidth;
                const clientWidth = alertBody.clientWidth;
                
                // Check if the container is horizontally scrollable
                const isScrollable = scrollWidth > clientWidth;

                if (isScrollable) {
                    // Prevent page scroll when scrolling horizontally
                    e.preventDefault(); // Prevent page scroll
                    e.stopPropagation();
                    e.stopImmediatePropagation();

                    // Handle horizontal scroll
                    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                        // Horizontal scroll: Shift + wheel or trackpad horizontal gesture
                        alertBody.scrollLeft += e.shiftKey ? e.deltaY : e.deltaX;
                    } else {
                        // Vertical wheel on horizontal container - convert to horizontal
                        alertBody.scrollLeft += e.deltaY;
                    }

                    return;
                }
            }
        };

        // Use capture phase to intercept before Lenis processes the event
        window.addEventListener("wheel", handleWheel, { passive: false, capture: true });

        // Store Lenis instance globally for PageTransition to access
        window.__lenis = lenis;

        const raf = (time) => {
            lenis.raf(time);
            requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);

        return () => {
            window.removeEventListener("wheel", handleWheel, { capture: true });
            lenis.destroy();
            if (window.__lenis === lenis) {
                delete window.__lenis;
            }
        };
    }, []);
};

// Enhanced AOS (Animate On Scroll) hook with stagger support - Optimized for faster loading
const useAOS = (sections, loading) => {
    useEffect(() => {
        if (!sections || sections.length === 0 || loading) return;

        let observer = null;
        const observedElements = [];

        // Use requestAnimationFrame instead of setTimeout for faster initialization
        const initAOS = () => {
            const observerOptions = {
                threshold: 0.15,
                rootMargin: "0px 0px -100px 0px",
            };

            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    requestAnimationFrame(() => {
                        if (entry.isIntersecting) {
                            const section = entry.target;

                            // Animate the section itself
                            if (!section.classList.contains("aos-animate")) {
                                section.classList.add("aos-animate");
                            }

                            // Trigger stagger animation for all child elements in this section
                            const staggerElements =
                                section.querySelectorAll("[data-stagger-index]");
                            staggerElements.forEach((el, idx) => {
                                // Special handling for lookout-card children - use their data-stagger-index value
                                const parentCard = el.closest(".lookout-card");
                                const isLookoutCardChild =
                                    parentCard && el !== parentCard;

                                if (isLookoutCardChild) {
                                    const staggerIndex =
                                        parseInt(
                                            el.getAttribute("data-stagger-index")
                                        ) || 0;
                                    // Wait for card to animate first (600ms) + stagger delay based on index
                                    const delay = 600 + staggerIndex * 150;
                                    setTimeout(() => {
                                        el.classList.add("stagger-animate");
                                    }, delay);
                                } else {
                                    // Regular stagger for other elements
                                    setTimeout(() => {
                                        el.classList.add("stagger-animate");
                                    }, idx * 10);
                                }
                            });
                        }
                    });
                });
            }, observerOptions);

            // Observe all sections with AOS classes
            const sectionsToObserve =
                document.querySelectorAll(".aos-fade-up");

            sectionsToObserve.forEach((element) => {
                if (!element.classList.contains("aos-animate")) {
                    // Check if section is already in view on page load
                    const rect = element.getBoundingClientRect();
                    const isInView =
                        rect.top < window.innerHeight * 0.8 && rect.bottom > 0;

                    // Check if it's a hero section - show immediately
                    const isHero = element.classList.contains("aos-hero") ||
                        element.querySelector(".hero") !== null;

                    if (isInView || isHero) {
                        // Section is already visible or is hero, trigger animation immediately
                        requestAnimationFrame(() => {
                        element.classList.add("aos-animate");
                        const staggerElements =
                            element.querySelectorAll("[data-stagger-index]");
                        staggerElements.forEach((el, idx) => {
                            const parentCard = el.closest(".lookout-card");
                            const isLookoutCardChild =
                                parentCard && el !== parentCard;

                            if (isLookoutCardChild) {
                                const staggerIndex =
                                    parseInt(
                                        el.getAttribute("data-stagger-index")
                                    ) || 0;
                                const delay = 600 + staggerIndex * 150;
                                setTimeout(() => {
                                    el.classList.add("stagger-animate");
                                }, delay);
                            } else {
                                    // Regular stagger for other elements
                                setTimeout(() => {
                                    el.classList.add("stagger-animate");
                                }, idx * 10);
                            }
                            });
                        });
                    } else {
                        observer.observe(element);
                        observedElements.push(element);
                    }
                }
            });
        };

        // Initialize immediately using requestAnimationFrame for faster execution
        requestAnimationFrame(initAOS);

        return () => {
            if (observer) {
                observedElements.forEach((element) => {
                    observer.unobserve(element);
                });
                observer.disconnect();
            }
        };
    }, [sections, loading]);
};

export default function Home() {
    const location = useLocation();
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setPageSeo } = useSeo();
    
    // Extract slug from route: "/" -> "home-page"
    const slug = location.pathname === "/" ? "home-page" : location.pathname.replace(/^\/+/, "");

    // 🔥 Initialize Lenis smooth scroll
    useLenis();

    // Initialize AOS animations
    useAOS(sections, loading);

    useEffect(() => {
        let isMounted = true;

        const applySections = (payload) => {
            if (!isMounted) return;
            const nextSections = Array.isArray(payload?.page_sections)
                ? payload.page_sections
                : [];
            setSections(nextSections);
            
            // Update SEO data
            if (payload?.seo) {
                setPageSeo(payload.seo);
            }
            
            setError(null);
            setLoading(false);
        };

        const handleError = (message) => {
            if (!isMounted) return;
            setError(message);
            setSections([]);
            setLoading(false);
        };

        const loadPage = async () => {
            setLoading(true);
            try {
                const res = await fetchPageByDynamicRouting(slug);
                applySections(res);
            } catch (err) {
                handleError("Unable to load the page at the moment.");
            } finally {
                setLoading(false);
            }
        };

        loadPage();

        return () => {
            isMounted = false;
        };
    }, [slug, setPageSeo]);

    // Always render page structure - show content immediately when available
    return (
        <div>
            {error && <div className="page-error">{error}</div>}
            {loading ? (
                <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="page-loading">Loading...</div>
                </div>
            ) : sections.length > 0 ? (
                <PageSections sections={sections} animateHero={true} />
            ) : null}
        </div>
    );
}
